import React, { forwardRef, memo, useImperativeHandle, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import Grid from '@mui/material/Grid';
import DialogContent from '@mui/material/DialogContent';
import Button from '@core/design-system/button';
import Chip from '@mui/material/Chip';
import DialogTitle from '@mui/material/DialogTitle';
import Tooltip from '@mui/material/Tooltip';
import { FiAlertCircle } from '@react-icons/all-files/fi/FiAlertCircle';

import {
  REAL_INCOME_CREATOR_OPTIONS,
  REAL_INCOME_ASSET_TYPE,
  GROSS_UP_OPTIONS,
  REAL_INCOME_ASSET_WITH_GROSS_UP_TYPE,
} from './constants';
import { IIncomeFixedModalHandles, IModalFixedIncome } from './model';
import { zodResolver } from '@hookform/resolvers/zod';
import { IncomeFixedSchemaType, incomeFixedSchema } from './schema';
import { PremiumTooltip } from '../premium/tooltip';
import { PremiumIcon } from '../premium/icon';
import { useAuth } from '@core/features-core/auth/client';
import {
  SelectHookForm,
  FormProvider,
  useForm,
  InputNumberHookForm,
} from '@core/design-system/input';
import { useRouter } from 'next/router';
import { UTMContent } from '../utm-button/enum';

export const ModalFixedIncome = memo(
  forwardRef<IIncomeFixedModalHandles, IModalFixedIncome>(
    ({ onSuccess }, ref) => {
      const [openDialog, setOpenDialog] = useState(false);
      const { userIsPremium } = useAuth();
      const router = useRouter();

      const formMethods = useForm<IncomeFixedSchemaType>({
        resolver: zodResolver(incomeFixedSchema),
        defaultValues: {
          indexer: REAL_INCOME_CREATOR_OPTIONS[0].value,
          indexerFee: 100,
          grossUp: null,
        },
      });

      useImperativeHandle(ref, () => {
        return { setOpenDialog };
      });

      const handleCloseDialog = () => {
        setOpenDialog(false);
      };

      const onSubmit = async ({
        indexer,
        indexerFee,
        grossUp,
      }: IncomeFixedSchemaType) => {
        const realIncomeConfig = REAL_INCOME_CREATOR_OPTIONS.find(
          ({ value }) => value === indexer
        );

        if (!realIncomeConfig) return;
        const isValidGrossUp = grossUp && userIsPremium;

        const percent = realIncomeConfig.percent || indexerFee;
        const accrual = realIncomeConfig.accrual || indexerFee;
        const signal = Number(accrual) >= 0 ? '+' : '-';
        const assetType = isValidGrossUp
          ? REAL_INCOME_ASSET_WITH_GROSS_UP_TYPE
          : REAL_INCOME_ASSET_TYPE;
        const gross_up = isValidGrossUp ? `|${grossUp}` : '';

        const realIncome = `${percent}|${realIncomeConfig.index}|${signal}${accrual}${gross_up}:${assetType}`;
        let nicename = realIncomeConfig.createName(indexerFee);
        nicename = isValidGrossUp
          ? `${nicename} com ${grossUp}% gross up`
          : nicename;

        onSuccess(realIncome, { nicename });

        formMethods.reset({});
      };

      return (
        <Dialog
          fullWidth={true}
          maxWidth="sm"
          open={openDialog}
          onClose={handleCloseDialog}
        >
          <DialogTitle>Adicionar Renda Fixa</DialogTitle>

          <FormProvider {...formMethods}>
            <form
              onSubmit={(e) => {
                e.stopPropagation();

                return formMethods.handleSubmit(onSubmit)(e);
              }}
            >
              <DialogContent>
                <Grid container spacing={1} rowSpacing={2}>
                  <Grid item xs={6} sm={6} md={6} lg={6}>
                    <SelectHookForm
                      label="Indexador"
                      id="indexer"
                      name="indexer"
                      placeholder="Indexador"
                      fullWidth
                      options={REAL_INCOME_CREATOR_OPTIONS}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={6} lg={6}>
                    <InputNumberHookForm
                      id="indexerFee"
                      name="indexerFee"
                      label="Taxa do indexador"
                      placeholder="100%"
                      isPercent
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={6} lg={6}>
                    <PremiumTooltip
                      utmContent={UTMContent.CTA_GROSS_UP}
                      description="Para adicionar o Gross-Up, além de outras vantagens exclusivas, confira o Retorno Prime."
                    >
                      <span>
                        <SelectHookForm
                          disabled={!userIsPremium}
                          label={
                            <>
                              {'Gross-up '}
                              <PremiumIcon size={13} />
                            </>
                          }
                          id="grossUp"
                          name="grossUp"
                          placeholder="Gross-up"
                          fullWidth
                          options={GROSS_UP_OPTIONS}
                        />
                      </span>
                    </PremiumTooltip>

                    <Tooltip
                      placement="right"
                      title="O Gross Up é uma técnica utilizada para comparar investimentos com diferentes alíquotas de impostos, tornando mais fácil avaliar ativos tributáveis e isentos de Imposto de Renda. Por exemplo, ao comparar uma LCI (isenta de imposto e rendendo 93% do CDI) com um CDB (tributável, rendendo 100% do CDI), o Gross Up simula um imposto sobre a LCI. Isso permite comparar de maneira justa ambos os investimentos como se estivessem sujeitos a impostos, facilitando a decisão de qual opção é mais vantajosa."
                    >
                      <Chip
                        icon={<FiAlertCircle />}
                        variant="filled"
                        label="Entenda o Gross-up"
                        sx={{
                          mt: 1,
                          backgroundColor: '#DDF1FA',
                          color: '#000',
                          p: 0,
                          height: 24,
                          '.MuiChip-label': {
                            fontWeight: 400,
                          },
                        }}
                      />
                    </Tooltip>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <Button
                  onClick={handleCloseDialog}
                  color="primary"
                  variant="outlined"
                  size="small"
                  type="button"
                >
                  CANCELAR
                </Button>

                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  size="small"
                >
                  SALVAR
                </Button>
              </DialogActions>
            </form>
          </FormProvider>
        </Dialog>
      );
    }
  )
);

export const ModalFixedIncomeRef = ({ forwardRef, ...props }: any) => (
  <ModalFixedIncome ref={forwardRef} {...(props as any)} />
);

export * from './model';
